// Rudimentary markup check of a translation against its original, for plain text fields that
// carry HTML or Liquid (theme editor content, notifications): are the target's tags balanced, and
// does it keep the original's Liquid placeholders. Attributes and text are not compared. Regex
// only and guarded by a cheap "has markup at all" look, so it can run per row and over a whole
// section's fields for the filter

const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
const TAG_RE = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)\b[^<>]*?(\/?)>/g;
const LIQUID_RE = /\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}/g;

export const mayHaveMarkup = (text) =>
  typeof text === 'string' && (text.indexOf('<') !== -1 || text.indexOf('{{') !== -1 || text.indexOf('{%') !== -1);

// a JSON structure (metafields, metaobject fields) is not checked: HTML inside its strings is escaped
// ("<\/p>"), so the tag rules would misread it, and its leaves are checked one by one in the JSON view
export const isJsonStructure = (text) => {
  if(typeof text !== 'string') return false;
  const trimmed = text.trim();
  if(!(trimmed.startsWith('{') || trimmed.startsWith('['))) return false;
  try { const parsed = JSON.parse(trimmed); return parsed !== null && typeof parsed === 'object'; } catch(e) { return false; }
};

// true when a tag is left open, closed twice or closed in the wrong order
const hasUnbalancedTags = (text) => {
  const stack = [];
  let m;
  TAG_RE.lastIndex = 0;
  while((m = TAG_RE.exec(text)) !== null) {
    const closing = m[1] === '/';
    const name = m[2].toLowerCase();
    const selfClosing = m[3] === '/';
    if(VOID_TAGS.has(name) || selfClosing) continue;
    if(closing) {
      if(stack.length === 0 || stack[stack.length - 1] !== name) return true;
      stack.pop();
    } else {
      stack.push(name);
    }
  }
  return stack.length > 0;
};

// the placeholders as a sorted list, whitespace inside them dropped ({{ shop.name }} = {{shop.name}})
const liquidTokens = (text) => (String(text).match(LIQUID_RE) || []).map(token => token.replace(/\s+/g, '')).sort();

const hasTags = (text) => { TAG_RE.lastIndex = 0; return TAG_RE.test(String(text)); };

// null when nothing is wrong (or there is nothing to check), otherwise the issue codes:
// 'unbalanced' (tags in the target), 'lost' (the original has tags, the target none at all - the
// formatting went missing; a restructured translation that keeps some tags is not flagged),
// 'liquid' (placeholders differ from the original)
export const markupIssues = (source, target) => {
  if(typeof target !== 'string' || target.trim() === '') return null;
  if(!mayHaveMarkup(source) && !mayHaveMarkup(target)) return null;
  if(isJsonStructure(source) || isJsonStructure(target)) return null;
  const issues = [];
  if(hasUnbalancedTags(target)) issues.push('unbalanced');
  if(hasTags(source) && !hasTags(target)) issues.push('lost');
  const a = liquidTokens(source), b = liquidTokens(target);
  if(a.length !== b.length || a.some((token, i) => token !== b[i])) issues.push('liquid');
  return issues.length > 0 ? issues : null;
};
